import { Address } from '@klear/address'
import type { OrganizationSize } from '@klear/constants'
import type { CountryCode } from '@klear/constants/countries'
import { Timestamp } from '@klear/time'

export type CustomerIdentity = { kind: 'new' } | { kind: 'saved'; id: number }

export function customerToJson(customer: Customer) {
  return {
    identity: customer.identity,
    traderId: customer.traderId,
    traderName: customer.traderName,
    name: customer.name,
    description: customer.description,
    address: customer.address.toJSON(),
    size: customer.size,
    website: customer.website,
    dunsNumber: customer.dunsNumber,
    lei: customer.lei,
    ein: customer.ein,
    vatNumber: customer.vatNumber,
    parentOrganization: customer.parentOrganization,
    allianzEhid: customer.allianzEhid,
    allianzRefreshedAt: customer.allianzRefreshedAt?.epochMs,
    createdAt: customer.createdAt.epochMs,
    updatedAt: customer.updatedAt.epochMs,
    deletedAt: customer.deletedAt?.epochMs,
    createdByUserId: customer.createdByUserId,
    createdByUserEmail: customer.createdByUserEmail,
    updatedByUserId: customer.updatedByUserId,
    updatedByUserEmail: customer.updatedByUserEmail,
    deletedByUserId: customer.deletedByUserId,
    deletedByUserEmail: customer.deletedByUserEmail,
    organizationId: customer.organizationId
  }
}
export type CustomerJson = ReturnType<typeof customerToJson>

export class Customer {
  static readonly __klearClass = 'Customer'

  readonly identity: CustomerIdentity
  traderId: number
  traderName: string
  name: string
  description: string
  address: Address
  size?: OrganizationSize
  website?: string
  dunsNumber?: string
  lei?: string
  ein?: string
  vatNumber?: string
  parentOrganization?: string
  allianzEhid?: string
  allianzRefreshedAt?: Timestamp
  createdAt: Timestamp
  updatedAt: Timestamp
  deletedAt?: Timestamp
  createdByUserId: number
  createdByUserEmail: string
  updatedByUserId?: number
  updatedByUserEmail?: string
  deletedByUserId?: number
  deletedByUserEmail?: string
  organizationId?: number

  constructor(props: CustomerJson) {
    const now = Timestamp.now()
    this.identity = props.identity?.kind === 'saved' ? { kind: 'saved', id: props.identity.id } : { kind: 'new' }
    this.address = new Address(props.address)
    this.traderId = props.traderId
    this.traderName = props.traderName
    this.name = props.name
    this.description = props.description ?? ''
    this.size = props.size
    this.website = props.website
    this.dunsNumber = props.dunsNumber
    this.lei = props.lei
    this.ein = props.ein
    this.vatNumber = props.vatNumber
    this.parentOrganization = props.parentOrganization
    this.allianzEhid = props.allianzEhid
    this.allianzRefreshedAt = props.allianzRefreshedAt != null ? new Timestamp(props.allianzRefreshedAt) : undefined
    this.createdAt = new Timestamp(props.createdAt ?? now)
    this.updatedAt = props.updatedAt ? new Timestamp(props.updatedAt) : this.createdAt
    this.deletedAt = props.deletedAt ? new Timestamp(props.deletedAt) : undefined
    this.createdByUserId = props.createdByUserId
    this.createdByUserEmail = props.createdByUserEmail
    this.updatedByUserId = props.updatedByUserId
    this.updatedByUserEmail = props.updatedByUserEmail
    this.deletedByUserId = props.deletedByUserId
    this.deletedByUserEmail = props.deletedByUserEmail
    this.organizationId = props.organizationId

    this.assertValid()
  }

  get id(): number | undefined {
    return this.identity.kind === 'saved' ? this.identity.id : undefined
  }

  get corporateId(): string | undefined {
    return this.dunsNumber ?? this.lei ?? this.ein ?? this.vatNumber ?? undefined
  }

  get country(): CountryCode {
    return this.address.country
  }

  set country(value: CountryCode) {
    this.address = this.address.withCountry(value)
  }

  get isSaved(): boolean {
    return this.identity.kind === 'saved'
  }

  requireSaved(): asserts this is this & { id: number } {
    if (!this.isSaved) {
      throw new Error('Customer is not saved')
    }
  }

  assertValid(): void {
    if (!this.traderId) {
      throw new Error('Customer trader is required')
    }
    if (!this.traderName.trim()) {
      throw new Error('Customer trader name is required')
    }
    if (!this.name.trim()) {
      throw new Error('Customer name is required')
    }
    if (!this.createdByUserEmail.trim()) {
      throw new Error('Customer createdByUserEmail is required')
    }
  }

  toJSON() {
    return customerToJson(this)
  }
}
